import React, { useRef, useState, useCallback, useEffect } from 'react';
import axiosInstance from '../services/axiosInstance';
import * as XLSX from 'xlsx';

const FormV2 = ({ formState, setFormState, disabled }) => {
    const debounceTimer = useRef(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [channelSearch, setChannelSearch] = useState('');
    const [isExistingFileDropdownOpen, setIsExistingFileDropdownOpen] = useState(false);
    const [isFundedValuesDropdownOpen, setIsFundedValuesDropdownOpen] = useState(false);
    const abortControllerRef = useRef(null);
    const dropdownRef = useRef(null);
    const existingFileDropdownRef = useRef(null);
    const fundedValuesDropdownRef = useRef(null);

    const [columns, setColumns] = useState([]);
    const [existingFileSearch, setExistingFileSearch] = useState('');

    // Reset local columns when the upload file is cleared (form reset)
    useEffect(() => {
        if (!formState.params.uploadFile) {
            setColumns([]);
            setChannelSearch('');
            setExistingFileSearch('');
        }
    }, [formState.params.uploadFile]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (existingFileDropdownRef.current && !existingFileDropdownRef.current.contains(event.target)) {
                setIsExistingFileDropdownOpen(false);
            }
            if (fundedValuesDropdownRef.current && !fundedValuesDropdownRef.current.contains(event.target)) {
                setIsFundedValuesDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchChannelsAndFiles = useCallback(async (buyerId, selectedButton) => {
        if (!buyerId || !selectedButton) {
            setFormState(prev => ({
                ...prev,
                params: {
                    ...prev.params,
                    channelIds: [],
                    existingFiles: []
                }
            }));
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            // 1. Fetch Channels first
            const { data: channelsData } = await axiosInstance.post('/api/get_unique_channel_ids', { buyerId, selectedButton }, { signal: controller.signal });

            const sortedChannels = [...(channelsData || [])].sort((a, b) => {
                const numA = Number(a);
                const numB = Number(b);
                const isNumA = a !== null && a !== '' && !isNaN(numA);
                const isNumB = b !== null && b !== '' && !isNaN(numB);
                if (isNumA && isNumB) {
                    return numA - numB;
                }
                return String(a).localeCompare(String(b));
            });

            const additionalValues = ["All channels separate", "All channels combined"];
            setFormState(prev => ({
                ...prev,
                params: {
                    ...prev.params,
                    channelIds: sortedChannels.length
                        ? [...sortedChannels, ...additionalValues]
                        : additionalValues
                },
                errors: {
                    ...prev.errors,
                    buyerId: ''
                }
            }));

            // 2. Then Fetch Existing Files
            const { data: filesData } = await axiosInstance.post('/api/get_exists_file_names', { buyerId, selectedButton }, { signal: controller.signal });

            setFormState(prev => ({
                ...prev,
                params: {
                    ...prev.params,
                    existingFiles: filesData || []
                }
            }));
        } catch (error) {
            if (error.name !== 'CanceledError') {
                console.error('Failed to fetch data:', error);
                // Distinguish "server responded with an error" from "the request
                // never reached the server" (network/CORS failure) - otherwise a
                // network outage reads as a generic/misleading data error.
                const backendErrorMsg = error.response
                    ? (error.response.data?.detail || 'Error fetching data')
                    : 'Could not reach the server. Please check your connection and try again.';
                setFormState(prev => ({
                    ...prev,
                    params: {
                        ...prev.params,
                        channelIds: [],
                        existingFiles: []
                    },
                    errors: {
                        ...prev.errors,
                        buyerId: backendErrorMsg
                    }
                }));
            }
        }
    }, [setFormState]);

    const handleBuyerIdChange = useCallback((e) => {
        const value = e.target.value.trim();

        setFormState(prev => ({
            ...prev,
            params: {
                ...prev.params,
                buyerId: value,
                channelIds: [],
                existingFiles: [],
                selectedExistingFile: ''
            },
            errors: {
                ...prev.errors,
                buyerId: '',
                setSelectedButtons: value && prev.params.setSelectedButtons.length === 0
                    ? 'Please select Payday or Personal'
                    : ''
            }
        }));

        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            if (value && formState.params.setSelectedButtons.length > 0) {
                fetchChannelsAndFiles(value, formState.params.setSelectedButtons[0]);
            }
        }, 1000);
    }, [fetchChannelsAndFiles, formState.params.setSelectedButtons, setFormState]);

    const handleButtonClick = useCallback((buttonName, event) => {
        event.preventDefault();

        setFormState(prev => {
            const newSelectedButtons = prev.params.setSelectedButtons.includes(buttonName)
                ? []
                : [buttonName];

            return {
                ...prev,
                params: {
                    ...prev.params,
                    setSelectedButtons: newSelectedButtons,
                    channelIds: [],
                    existingFiles: []
                },
                errors: {
                    ...prev.errors,
                    setSelectedButtons: '',
                    buyerId: ''
                }
            };
        });

        clearTimeout(debounceTimer.current);
        debounceTimer.current = setTimeout(() => {
            if (formState.params.buyerId && (formState.params.setSelectedButtons.includes(buttonName) ? [] : [buttonName]).length > 0) {
                fetchChannelsAndFiles(formState.params.buyerId, buttonName);
            }
        }, 500);

    }, [fetchChannelsAndFiles, formState.params.buyerId, formState.params.setSelectedButtons, setFormState]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setFormState(prev => ({
            ...prev,
            params: {
                ...prev.params,
                uploadFile: file
            },
            errors: {
                ...prev.errors,
                uploadFile: ''
            }
        }));

        const reader = new FileReader();
        reader.onload = (evt) => {
            const bstr = evt.target.result;
            const wb = XLSX.read(bstr, { type: 'binary' });
            const wsname = wb.SheetNames[0];
            const ws = wb.Sheets[wsname];
            const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
            if (data.length > 0) {
                const headers = data[0];
                setColumns(headers);

                // Keep the JSON data to extract unique funded values later
                const jsonData = XLSX.utils.sheet_to_json(ws);
                setFormState(prev => ({
                    ...prev,
                    params: {
                        ...prev.params,
                        fileData: jsonData
                    }
                }));
            }
        };
        reader.readAsBinaryString(file);

        // Clear the input value so the same file can be uploaded again if needed
        e.target.value = null;
    };

    const handleIdentifierChange = (e) => {
        setFormState(prev => ({
            ...prev,
            params: {
                ...prev.params,
                identifierColumn: e.target.value
            },
            errors: {
                ...prev.errors,
                identifierColumn: ''
            }
        }));
    };

    const handleFundedColumnChange = (e) => {
        const selectedCol = e.target.value;
        setFormState(prev => {
            let uniqueValues = [];
            if (prev.params.fileData && selectedCol) {
                const vals = prev.params.fileData.map(row => row[selectedCol]).filter(v => v !== undefined && v !== null);
                uniqueValues = [...new Set(vals)];
            }
            return {
                ...prev,
                params: {
                    ...prev.params,
                    fundedColumn: selectedCol,
                    availableFundedValues: uniqueValues,
                    selectedFundedValues: []
                },
                errors: {
                    ...prev.errors,
                    fundedColumn: ''
                }
            };
        });
    };

    const handleDateChange = (field) => (e) => {
        const dateValue = e.target.value ? new Date(e.target.value) : null;
        setFormState(prev => ({
            ...prev,
            params: {
                ...prev.params,
                [field]: dateValue
            },
            errors: {
                ...prev.errors,
                [field]: ''
            }
        }));
    };

    const toggleDropdown = () => {
        setIsDropdownOpen((prevState) => !prevState);
    };

    const handleChannelSelection = (id) => {
        const { selectedChannelIds, channelIds } = formState.params;
        const selectedValue = channelIds.find(channelId => channelId === id);

        if (typeof selectedValue === 'string') {
            setFormState(prevState => {
                const isAlreadySelected = prevState.params.channels_with_names.some(
                    group => group.channels === selectedValue
                );

                if (isAlreadySelected) {
                    return {
                        ...prevState,
                        params: {
                            ...prevState.params,
                            channels_with_names: prevState.params.channels_with_names.filter(
                                group => group.channels !== selectedValue
                            )
                        }
                    };
                } else {
                    return {
                        ...prevState,
                        params: {
                            ...prevState.params,
                            channels_with_names: [
                                ...prevState.params.channels_with_names,
                                { channels: selectedValue, group_name: "" }
                            ]
                        }
                    };
                }
            });
            return;
        }

        if (selectedChannelIds.includes(id)) {
            setFormState(prevState => ({
                ...prevState,
                params: {
                    ...prevState.params,
                    selectedChannelIds: selectedChannelIds.filter(channelId => channelId !== id)
                }
            }));
        } else {
            setFormState(prevState => ({
                ...prevState,
                params: {
                    ...prevState.params,
                    selectedChannelIds: [...selectedChannelIds, id]
                }
            }));
        }
    };

    const handleFundedValueSelection = (val) => {
        const { selectedFundedValues } = formState.params;
        if (selectedFundedValues.includes(val)) {
            setFormState(prevState => ({
                ...prevState,
                params: {
                    ...prevState.params,
                    selectedFundedValues: selectedFundedValues.filter(v => v !== val)
                }
            }));
        } else {
            setFormState(prevState => ({
                ...prevState,
                params: {
                    ...prevState.params,
                    selectedFundedValues: [...selectedFundedValues, val]
                }
            }));
        }
    };

    const handleGroupChannels = () => {
        const { selectedChannelIds } = formState.params;
        if (selectedChannelIds.length === 0) return;
        const newGroup = { channels: selectedChannelIds, group_name: '' };
        setFormState(prevState => ({
            ...prevState,
            params: {
                ...prevState.params,
                selectedChannels: [...prevState.params.selectedChannels, selectedChannelIds],
                selectedChannelIds: [],
                channels_with_names: [...prevState.params.channels_with_names, newGroup],
            },
        }));
    };

    const handleRemoveChannel = (channelList) => {
        setFormState(prev => ({
            ...prev,
            params: {
                ...prev.params,
                selectedChannels: prev.params.selectedChannels.filter(
                    selectedList => !arraysAreEqual(selectedList, channelList)
                ),
                channels_with_names: prev.params.channels_with_names.filter(
                    group => !arraysAreEqual(group.channels, channelList)
                ),
            },
        }));
    };

    const handleGroupNameChange = (channelList, e) => {
        const newGroupName = e.target.value;
        setFormState(prev => {
            const updatedGroups = prev.params.channels_with_names.map(group =>
                arraysAreEqual(group.channels, channelList)
                    ? { ...group, group_name: newGroupName }
                    : group
            );
            return {
                ...prev,
                params: {
                    ...prev.params,
                    channels_with_names: updatedGroups
                }
            };
        });
    };

    const formatDateForInput = (date) => {
        if (!date || !(date instanceof Date)) return '';
        return date.toISOString().split('T')[0];
    };

    const arraysAreEqual = (arr1, arr2) => {
        if (typeof arr1 === 'string' || typeof arr2 === 'string') return arr1 === arr2;
        if (arr1.length !== arr2.length) return false;
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) return false;
        }
        return true;
    };

    return (
        <form>
            <div className="form-section">

                {/* ━━━ SECTION 1 · FILE CONFIGURATION ━━━ */}
                <div className="v2-section-label">① File Configuration</div>

                {/* Upload */}
                <div className="v2-full-row">
                    <div className="form-group">
                        <label>Upload Excel File</label>
                        <div className="file-upload-box">
                            <span className="file-upload-icon">
                                {formState.params.uploadFile ? '✅' : '📄'}
                            </span>
                            <span className="file-upload-text">
                                {formState.params.uploadFile
                                    ? `${formState.params.uploadFile.name}  ·  ${(formState.params.uploadFile.size / 1024).toFixed(1)} KB`
                                    : 'Click to browse or drag Excel file here'}
                            </span>
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleFileUpload}
                                disabled={disabled}
                                className="file-upload-input"
                            />
                        </div>
                        {formState.errors.uploadFile && (
                            <p className="error-message">{formState.errors.uploadFile}</p>
                        )}
                    </div>
                </div>

                {/* File Label */}
                {formState.params.uploadFile && (
                    <div className="v2-full-row">
                        <div className="form-group">
                            <label>File Label</label>
                            <input
                                type="text"
                                placeholder="Enter a name for the output file"
                                value={formState.params.fileLabel || ''}
                                onChange={(e) =>
                                    setFormState(prev => ({
                                        ...prev,
                                        params: { ...prev.params, fileLabel: e.target.value }
                                    }))
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                )}

                {/* Column selectors — appear after file is loaded */}
                {columns.length > 0 && (
                    <div className="form-section-1">
                        <div className="form-container-2">
                            <div className="form-group">
                                <label>Identifier Column</label>
                                <select
                                    value={formState.params.identifierColumn || ''}
                                    onChange={handleIdentifierChange}
                                    disabled={disabled}
                                >
                                    <option value="">Select Column</option>
                                    {columns.map((col, idx) => (
                                        <option key={idx} value={col}>{col}</option>
                                    ))}
                                </select>
                                {formState.errors.identifierColumn && (
                                    <p className="error-message">{formState.errors.identifierColumn}</p>
                                )}
                            </div>
                        </div>
                        <div className="form-container-1">
                            <div className="form-group">
                                <label>FUNDED Column</label>
                                <select
                                    value={formState.params.fundedColumn || ''}
                                    onChange={handleFundedColumnChange}
                                    disabled={disabled}
                                >
                                    <option value="">Select Column</option>
                                    {columns.map((col, idx) => (
                                        <option key={idx} value={col}>{col}</option>
                                    ))}
                                </select>
                                {formState.errors.fundedColumn && (
                                    <p className="error-message">{formState.errors.fundedColumn}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Funded values — appear after funded column is selected */}
                {formState.params.availableFundedValues?.length > 0 && (
                    <div className="v2-full-row">
                        <div className="form-group group-ids">
                            <label>
                                Target Funded Values
                                {formState.params.selectedFundedValues?.length > 0 && (
                                    <span className="v2-count-badge">{formState.params.selectedFundedValues.length} selected</span>
                                )}
                            </label>
                            <div className="dropdown" ref={fundedValuesDropdownRef}>
                                <div
                                    className={`dropdown-toggle ${disabled ? 'disabled' : ''}`}
                                    onClick={() => !disabled && setIsFundedValuesDropdownOpen(!isFundedValuesDropdownOpen)}
                                >
                                    <span>
                                        {formState.params.selectedFundedValues?.length > 0
                                            ? formState.params.selectedFundedValues.join(', ')
                                            : 'Select Funded Values'}
                                    </span>
                                    <span className="dropdown-arrow">▼</span>
                                </div>
                                {isFundedValuesDropdownOpen && !disabled && (
                                    <div className="dropdown-menu">
                                        {formState.params.availableFundedValues.map((val, index) => {
                                            const isChecked = formState.params.selectedFundedValues.includes(val);
                                            return (
                                                <div key={index} className={`dropdown-item ${isChecked ? 'selected' : ''}`}>
                                                    <label>
                                                        <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={() => !disabled && handleFundedValueSelection(val)}
                                                            disabled={disabled}
                                                        />
                                                        {val}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}


                <div className="v2-divider" />

                {/* ━━━ SECTION 2 · DATA SOURCE ━━━ */}
                <div className="v2-section-label">② Data Source</div>

                {/* Product Type + Buyer ID */}
                <div className="form-section-1">
                    <div className="form-container-2">
                        <div className="toggle-container">
                            <button
                                onClick={(e) => handleButtonClick('Payday', e)}
                                className={`view-button ${formState.params.setSelectedButtons.includes('Payday') ? 'active' : ''}`}
                                disabled={disabled}
                            >
                                Payday
                            </button>
                            <button
                                onClick={(e) => handleButtonClick('Personal', e)}
                                className={`view-button ${formState.params.setSelectedButtons.includes('Personal') ? 'active' : ''}`}
                                disabled={disabled}
                            >
                                Personal
                            </button>
                        </div>
                        {formState.errors.setSelectedButtons && (
                            <p className="error-message error-message-p">{formState.errors.setSelectedButtons}</p>
                        )}
                    </div>
                    <div className="form-container-1">
                        <div className="form-group group-ids">
                            <label>BUYER_ID</label>
                            <input
                                type="text"
                                placeholder="BUYER_ID"
                                value={formState.params.buyerId || ''}
                                onChange={handleBuyerIdChange}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </div>

                {/* Buyer ID error — full width, own row, so a longer message
                    (e.g. a network failure) never overlaps the narrow input above it */}
                {formState.errors.buyerId && (
                    <div className="v2-full-row">
                        <p className="error-message error-message-p">{formState.errors.buyerId}</p>
                    </div>
                )}

                {/* Channel IDs — full width, appears after buyer ID is set */}
                {formState.params.buyerId && formState.params.channelIds.length > 0 && (
                    <div className="v2-full-row">
                        <div className="form-group group-ids">
                            <label>
                                CHANNEL_IDS
                                {formState.params.channels_with_names.length > 0 && (
                                    <span className="v2-count-badge">{formState.params.channels_with_names.length} group{formState.params.channels_with_names.length > 1 ? 's' : ''}</span>
                                )}
                            </label>
                            <div className="dropdown" ref={dropdownRef}>
                                <div
                                    className={`dropdown-toggle ${disabled ? 'disabled' : ''}`}
                                    onClick={() => !disabled && toggleDropdown()}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <button
                                            className="group-btn"
                                            onClick={(e) => { e.stopPropagation(); if (!disabled) handleGroupChannels(); }}
                                            type="button"
                                            disabled={disabled}
                                        >
                                            Group
                                        </button>
                                        <span className="dropdown-placeholder">
                                            {formState.params.selectedChannelIds.length > 0
                                                ? `${formState.params.selectedChannelIds.length} selected — click Group to confirm`
                                                : 'Select a CHANNEL_ID'}
                                        </span>
                                    </div>
                                    <span className="dropdown-arrow">▼</span>
                                </div>
                                {isDropdownOpen && !disabled && (
                                    <div className="dropdown-menu dropdown-menu-with-search">
                                        <div className="dropdown-search" style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                            <input
                                                type="text"
                                                placeholder="Search channel..."
                                                value={channelSearch}
                                                onChange={(e) => setChannelSearch(e.target.value)}
                                                style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="dropdown-items-container" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                            {formState.params.channelIds.filter(id => String(id).toLowerCase().includes(channelSearch.toLowerCase())).map((id, index) => {
                                                const isChecked = formState.params.selectedChannelIds.includes(id);
                                                return (
                                                    <div key={index} className={`dropdown-item ${isChecked ? 'selected' : ''}`}>
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => !disabled && handleChannelSelection(id)}
                                                                disabled={disabled}
                                                            />
                                                            {id}
                                                        </label>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Grouped channel tags — stacked below, full width */}
                            {formState.params.channels_with_names.length > 0 && (
                                <div className="selected-channel-ids" style={{ marginTop: '10px' }}>
                                    {formState.params.channels_with_names.map((group, index) => (
                                        <div key={index} className="channel-id-box">
                                            <span>
                                                {typeof group.channels === 'string'
                                                    ? group.channels
                                                    : group.channels.join(', ')}
                                            </span>
                                            <input
                                                type="text"
                                                placeholder="Add Name"
                                                className="group-name-input"
                                                value={group.group_name}
                                                onChange={(e) => handleGroupNameChange(group.channels, e)}
                                                disabled={disabled}
                                            />
                                            <button
                                                type="button"
                                                className="remove-btn"
                                                onClick={() => handleRemoveChannel(group.channels)}
                                                disabled={disabled}
                                            >
                                                &#8722;
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Compare with previous run */}
                {formState.params.existingFiles?.length > 0 && (
                    <div className="v2-full-row">
                        <div className="form-group group-ids">
                            <label>Compare With Previous Run</label>
                            <div className="dropdown" ref={existingFileDropdownRef}>
                                <div
                                    className={`dropdown-toggle ${disabled ? 'disabled' : ''}`}
                                    onClick={() => !disabled && setIsExistingFileDropdownOpen(!isExistingFileDropdownOpen)}
                                >
                                    <span className={formState.params.selectedExistingFile ? '' : 'dropdown-placeholder'}>
                                        {formState.params.selectedExistingFile || 'None (Creates new isolated run)'}
                                    </span>
                                    <span className="dropdown-arrow">▼</span>
                                </div>
                                {isExistingFileDropdownOpen && !disabled && (
                                    <div className="dropdown-menu dropdown-menu-with-search">
                                        <div className="dropdown-search" style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                                            <input
                                                type="text"
                                                placeholder="Search file..."
                                                value={existingFileSearch}
                                                onChange={(e) => setExistingFileSearch(e.target.value)}
                                                style={{ width: '100%', padding: '6px', boxSizing: 'border-box' }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className="dropdown-items-container">
                                            <div
                                                className={`dropdown-item ${!formState.params.selectedExistingFile ? 'selected' : ''}`}
                                                onClick={() => {
                                                    setFormState(prev => ({ ...prev, params: { ...prev.params, selectedExistingFile: '' } }));
                                                    setIsExistingFileDropdownOpen(false);
                                                }}
                                            >
                                                None
                                            </div>
                                            {formState.params.existingFiles.filter(filename => filename.toLowerCase().includes(existingFileSearch.toLowerCase())).map((filename, index) => (
                                                <div
                                                    key={index}
                                                    className={`dropdown-item ${formState.params.selectedExistingFile === filename ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setFormState(prev => ({ ...prev, params: { ...prev.params, selectedExistingFile: filename } }));
                                                        setIsExistingFileDropdownOpen(false);
                                                    }}
                                                >
                                                    {filename}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Report Type — full width, own row */}
                <div className="v2-full-row">
                    <div className="form-group">
                        <label>Report Type</label>
                        <div className="segmented-control">
                            <button
                                type="button"
                                className={`segmented-option ${formState.params.reportType === 'bad' ? 'active' : ''}`}
                                onClick={() => setFormState(prev => ({
                                    ...prev,
                                    params: { ...prev.params, reportType: 'bad' }
                                }))}
                                disabled={disabled}
                            >
                                Bad Performing Sources
                            </button>
                            <button
                                type="button"
                                className={`segmented-option ${formState.params.reportType === 'best' ? 'active' : ''}`}
                                onClick={() => setFormState(prev => ({
                                    ...prev,
                                    params: { ...prev.params, reportType: 'best' }
                                }))}
                                disabled={disabled}
                            >
                                Best Performing Sources
                            </button>
                        </div>
                    </div>
                </div>

                <div className="v2-divider" />

                {/* ━━━ SECTION 3 · ANALYTICS PERIOD ━━━ */}
                <div className="v2-section-label">③ Analytics Period</div>

                <div className="form-section-3">
                    <div className="form-group-current">
                        <div className="group-dates">
                            <div className="form-group group-date">
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    value={formatDateForInput(formState.params.current_startDate)}
                                    onChange={handleDateChange('current_startDate')}
                                    disabled={disabled}
                                />
                                {formState.errors.current_startDate && (
                                    <p className="error-message">{formState.errors.current_startDate}</p>
                                )}
                            </div>
                            <div className="form-group group-date">
                                <label>End Date</label>
                                <input
                                    type="date"
                                    value={formatDateForInput(formState.params.current_endDate)}
                                    onChange={handleDateChange('current_endDate')}
                                    disabled={disabled}
                                />
                                {formState.errors.current_endDate && (
                                    <p className="error-message">{formState.errors.current_endDate}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </form>
    );
};

export default FormV2;
